/** Questionnaire PPCO (Plus / Potentiel / Craintes / Opportunités) — référence fonctionnelle ISBD. */

export interface PpcoQuestion {
  id: string;
  question: string;
}

export interface PpcoSection {
  key: 'plus' | 'potentiel' | 'craintes' | 'opportunites';
  title: string;
  description: string;
  /** Note max indicative attribuée par l’IA (craintes négatives). */
  noteMax: number;
  questions: PpcoQuestion[];
}

export const PPCO_SECTIONS: PpcoSection[] = [
  {
    key: 'plus',
    title: 'PLUS',
    description: 'Identifier ce qui est déjà fort et attractif dans l’idée',
    noteMax: 5,
    questions: [
      { id: 'plus_q1', question: 'Quels sont les points forts de cette idée ?' },
      { id: 'plus_q2', question: 'Quels sont ses atouts ?' },
      { id: 'plus_q3', question: 'Qu’aimez-vous dans cette idée ?' },
      { id: 'plus_q4', question: 'En quoi cette idée est-elle innovante ?' },
    ],
  },
  {
    key: 'potentiel',
    title: 'POTENTIEL',
    description: 'Explorer ce que l’idée pourrait devenir au maximum',
    noteMax: 5,
    questions: [
      {
        id: 'potentiel_q1',
        question: 'Quels seraient les avantages futurs si vous déployez cette idée avec succès ?',
      },
      {
        id: 'potentiel_q2',
        question: 'Quels impacts positifs sur l’entreprise ou l’équipe ?',
      },
    ],
  },
  {
    key: 'craintes',
    title: 'CRAINTES',
    description: 'Identifier les obstacles, risques et faiblesses',
    noteMax: -20,
    questions: [
      { id: 'craintes_q1', question: 'Quelles sont vos préoccupations sur cette idée ?' },
      { id: 'craintes_q2', question: 'Quels sont les freins, blocages ou risques ?' },
      { id: 'craintes_q3', question: 'Quels sont ses inconvénients ou limites ?' },
    ],
  },
  {
    key: 'opportunites',
    title: 'OPPORTUNITÉS',
    description: 'Transformer les préoccupations en pistes d’amélioration',
    noteMax: 10,
    questions: [
      {
        id: 'opportunites_q1',
        question: 'Quelles solutions pour lever les obstacles notés ?',
      },
      {
        id: 'opportunites_q2',
        question: 'Quelles parades ou moyens pour surmonter ces problèmes ?',
      },
      {
        id: 'opportunites_q3',
        question: 'Comment minimiser ou éviter ces problèmes ?',
      },
    ],
  },
];

export type PpcoAnswers = Record<string, string>;

export function emptyPpcoAnswers(): PpcoAnswers {
  const out: PpcoAnswers = {};
  for (const section of PPCO_SECTIONS) {
    for (const q of section.questions) out[q.id] = '';
  }
  return out;
}

export function ppcoAnswersComplete(answers: PpcoAnswers): boolean {
  return PPCO_SECTIONS.every((s) =>
    s.questions.every((q) => (answers[q.id] ?? '').trim().length >= 12),
  );
}

export interface PpcoSectionScore {
  key: string;
  title: string;
  score: number;
  comment: string;
}

export interface PpcoEvaluation {
  sections: PpcoSectionScore[];
  score_final: number;
  conclusion: string;
  recommendation: string;
}

export function synthesizePpcoConclusion(score: number): string {
  if (score >= 12) return 'Idée fortement soutenue — poursuite recommandée';
  if (score >= 5) return 'Idée prometteuse — à consolider avant lancement';
  if (score >= 0) return 'Idée mitigée — corrections nécessaires';
  return 'Idée fragile — pivot ou abandon à considérer';
}
